import { useEffect, useLayoutEffect, useRef } from 'react';
import { aQuiVaLEchap, type SorteDEchap } from './ordreDEchap';

/**
 * La seule écoute d'Échap de l'app (chantier `nettoyage-fiche`, ticket 02). Ce qui s'ouvre s'y
 * inscrit, avec sa sorte ; à chaque Échap, `aQuiVaLEchap` dit qui ferme. Une écoute en
 * descendant (avant les champs, pour `avant-tout`) et une en remontant (après eux), sur la
 * fenêtre, posées tant que quelque chose est ouvert.
 */
interface Ouvert {
  sorte: SorteDEchap;
  fermer: () => void;
}

/** Ce qui est ouvert, du plus ancien au plus récent. */
const ouverts: Ouvert[] = [];

function repondre(e: KeyboardEvent, passage: 'capture' | 'bulle') {
  if (e.key !== 'Escape') return;
  // Une copie : fermer peut retirer quelqu'un de la liste.
  const ceuxLa = [...ouverts];
  const fermes = aQuiVaLEchap(
    ceuxLa.map((o) => o.sorte),
    passage
  );
  if (fermes.length === 0) return;
  if (passage === 'capture') e.stopImmediatePropagation();
  for (const i of fermes) ceuxLa[i]?.fermer();
}

const enDescendant = (e: KeyboardEvent) => repondre(e, 'capture');
const enRemontant = (e: KeyboardEvent) => repondre(e, 'bulle');

function inscrire(ouvert: Ouvert): () => void {
  if (ouverts.length === 0) {
    window.addEventListener('keydown', enDescendant, true);
    window.addEventListener('keydown', enRemontant);
  }
  ouverts.push(ouvert);
  return () => {
    const i = ouverts.indexOf(ouvert);
    if (i !== -1) ouverts.splice(i, 1);
    if (ouverts.length === 0) {
      window.removeEventListener('keydown', enDescendant, true);
      window.removeEventListener('keydown', enRemontant);
    }
  };
}

/**
 * Rien n'est ouvert qui écoute Échap (ni fiche, ni panneau, ni menu, ni Hiérarchiser) : les
 * touches de la carte (chantier modes-de-la-carte, modification 02) ne partent qu'alors. Lu au
 * moment de la touche : ce qu'Échap vient de fermer y est encore, et passe d'abord.
 */
export function rienNEcouteEchap(): boolean {
  return ouverts.length === 0;
}

/**
 * Tant que `ouvert` est vrai, Échap appelle `fermer`, selon l'ordre de `aQuiVaLEchap`. Sa place
 * est celle du moment où il s'est ouvert ; `fermer` est toujours celui du dernier rendu.
 */
export function useEchap(ouvert: boolean, fermer: () => void, sorte: SorteDEchap = 'pile'): void {
  const dernier = useRef(fermer);
  useLayoutEffect(() => {
    dernier.current = fermer;
  });

  useEffect(() => {
    if (!ouvert) return;
    return inscrire({ sorte, fermer: () => dernier.current() });
  }, [ouvert, sorte]);
}
